import { Theme as AugmentedTheme } from '@mui/material/styles';

import { makeStyles } from 'tss-react/mui';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useParams } from 'react-router-dom';
import {
  ApiButton,
  Box,
  Button,
  ContentTemplate,
  Divider,
  Grid,
  HeaderTemplate,
  KeyValue,
  Progress,
  SectionHeader,
  Stepper,
  TitleHeader,
  Typography,
} from 'web-library';
import RouteKey from '../../routes/key';
import PersonalDetails from '../../modules/appeal/components/@candidate/PersonalDetails';
import ExamDetails from '../../modules/appeal/components/@candidate/ExamLevelDetails';
import SubjectSelectionTable
  from '../../modules/appeal/components/@candidate/select/SubjectSelectionTable';
import {
  useGetCoordinatorAppealSelectSubject
} from '../../api-refactored/appeal/useGetCoordinatorAppealSelectSubject';
import {
  SaveUpdateSubjectRequest,
  usePutCoordinatorAppealSaveUpdateSubject,
} from '../../api-refactored/appeal/usePutCoordinatorAppealSaveUpdateSubject';

const { appeal } = RouteKey.child;
const { reviewApplication, applicationsCoordinator } = RouteKey.appeal.child;

const useStyles = makeStyles()((theme) =>
({
  stepper: {
    position: 'relative',
    left: '-9.5%',
    [theme.breakpoints.between('sm', 'lg')]: {
      left: '-10%',
    },
    [theme.breakpoints.between('xs', 'md')]: {
      left: '-5%',
    },
  },
  container: {
    alignItems: 'center',
  },
  dataTableContainer: {
    marginBottom: theme.spacing(5),
  },
  button: {
    height: 'fit-content',
    marginRight: theme.spacing(2),
    [theme.breakpoints.down('sm')]: {
      marginRight: theme.spacing(1),
    },
  },
  errorText: {
    color: theme.color.state.stateError,
  },
  buttonContainer: {
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(7),
  },
}));

interface CoordinatorSelectSubjectProps {
  title: string;
  routeSteps: { label: string }[];
}

const CoordinatorSelectSubject = ({ title, routeSteps }: CoordinatorSelectSubjectProps) => {
  const { classes } = useStyles();
  const { t } = useTranslation();
  const history = useHistory();
  const { uuid } = useParams<{ uuid: string }>();

  const [selectionArr, setSelectionArr] = useState<number[]>([]);
  const [errMsg, setErrMsg] = useState<string>('');

  const {
    data: examListingData,
    isLoading: isLoadingExamListingData,
  } = useGetCoordinatorAppealSelectSubject({
    uuid,
    onSuccess: (data) => {
      const selectedSubject: number[] = [];
      data.subjectResultModels.forEach((x, index) => {
        if (x.selected) {
          selectedSubject.push(index);
        }
      });
      setSelectionArr(selectedSubject);
    },
  });

  const {
    mutate: mutatePutSaveUpateSubject,
    status: statusSaveUpdateSubject,
  } = usePutCoordinatorAppealSaveUpdateSubject({
    uuid,
    onSuccess: () =>
      history.push(
        `/${appeal}/${applicationsCoordinator.route}/${uuid}/${reviewApplication.route}`
      ),
    onError: (err) => {
      const { message } = err?.response?.data || {};
      setErrMsg(message || "An unknown error occurred");
    },
  });

  const handleProceedClick = () => {
    if (examListingData) {
      const selectedSubjectCodes = selectionArr.map((subjectIndex) =>
        examListingData.subjectResultModels[subjectIndex].subjectCode.toString()
      );

      const requestBody: SaveUpdateSubjectRequest = {
        examCode: examListingData.examCode,
        examSeriesCode: examListingData.examSeriesCode,
        examYear: examListingData.examYear,
        subjectCodes: selectedSubjectCodes,
      };

      mutatePutSaveUpateSubject(requestBody);
    }
  };

  const onBack = () => {
    history.goBack();
  };

  return (
    <>
      <TitleHeader title={title} />
      <HeaderTemplate>
        {examListingData && (
          <KeyValue
            keyText={t('appeal->>AppealCoordinatorSelectSubject.ExamLevel')}
            valueText={examListingData.examLevel}
          />
        )}
        <Box className={classes.stepper}>
          <Stepper activeStep={1} steps={routeSteps} />
        </Box>
      </HeaderTemplate>
      {isLoadingExamListingData ? (
        <ContentTemplate>
          <Progress color="secondary" />
        </ContentTemplate>
      ) : (
        <Box>
          {examListingData && (
            <>
              <ContentTemplate isContinuable type="full">
                <SectionHeader title={t('appeal->>SelectSubject.Select Subject(s)')} />
                <PersonalDetails
                  statutoryName={examListingData.candidateName}
                  nricFin={examListingData.candidateId}
                  citizenship={examListingData.candidateCitizenship}
                  schoolName={examListingData.examSchool}
                  indexNo={examListingData.candidateIndexNum}
                />
                <Divider />
                <ExamDetails
                  examLevel={examListingData.examLevel}
                  examSeries={examListingData.examSeries}
                  examYear={examListingData.examYear.toString()}
                />
                {examListingData.subjectResultModels && (
                  <Box className={classes.dataTableContainer}>
                    <SubjectSelectionTable
                      examData={examListingData.subjectResultModels}
                      selectedSubjects={selectionArr}
                      onSelectSubject={setSelectionArr}
                    />
                  </Box>
                )}
              </ContentTemplate>
              <ContentTemplate continuous>
                {errMsg && (
                  <Grid xs={12} md={8}>
                    <Typography className={classes.errorText} display="inline"
                      variant="body2">
                      {errMsg}
                    </Typography>
                  </Grid>
                )}
                <Grid
                  container
                  direction="row"
                  alignItems="flex-start"
                  spacing={3}
                  className={classes.buttonContainer}
                >
                  <Grid xs={12} md={5}>
                    <Button
                      id="button-selectSubject-back"
                      fullWidth
                      className={classes.button}
                      variant="outlined"
                      onClick={() => {
                        onBack();
                      }}
                    >
                      {t('appeal->>AppealCoordinatorSelectSubject.SelectAnotherCandidate')}
                    </Button>
                  </Grid>

                  <Grid xs={12} md={5}>
                    <ApiButton
                      id="button-selectSubject-continueToNextPage"
                      status={statusSaveUpdateSubject}
                      fullWidth
                      className={classes.button}
                      onClick={handleProceedClick}
                      disabled={selectionArr.length === 0}
                    >
                      {t(`appeal:SelectSubject.Proceed with {{subjectCount}} Subject(s)`, {
                        subjectCount: selectionArr.length,
                      })}
                    </ApiButton>
                  </Grid>
                </Grid>
              </ContentTemplate>
            </>
          )}
        </Box>
      )}
    </>
  );
};

export default CoordinatorSelectSubject;
